import React, { useState, useRef } from 'react';
import { Upload, File, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  CategoriaArquivo,
  EXTENSOES_PERMITIDAS,
  rotuloExtensoes,
  validarArquivo,
} from '../utils/arquivos';

interface TempFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
}

interface TempFileUploadProps {
  onFilesChange?: (files: File[]) => void;
  maxFiles?: number;
  maxSizePerFile?: number; // em MB
  // Define quais extensões são aceitas ('evidencia' por padrão)
  categoria?: CategoriaArquivo;
}

const TempFileUpload: React.FC<TempFileUploadProps> = ({
  onFilesChange,
  maxFiles = 10,
  maxSizePerFile = 10,
  categoria = 'evidencia',
}) => {
  const extensoes = EXTENSOES_PERMITIDAS[categoria];
  const [files, setFiles] = useState<TempFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validação por extensão: o tipo MIME informado pelo navegador varia por sistema
  // (ex.: .zip no Windows vem como "application/x-zip-compressed")
  const validateFile = (file: File): string | null =>
    validarArquivo(file, extensoes, maxSizePerFile);

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const fileArray = Array.from(selectedFiles);
    const validFiles: TempFile[] = [];
    const errors: string[] = [];

    // Verificar limite de arquivos
    if (files.length + fileArray.length > maxFiles) {
      toast.error(`Máximo de ${maxFiles} arquivos permitidos`);
      return;
    }

    fileArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push({
          id: `${Date.now()}_${Math.random()}`,
          file,
          name: file.name,
          size: file.size,
          type: file.type,
        });
      }
    });

    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
    }

    if (validFiles.length > 0) {
      const newFiles = [...files, ...validFiles];
      setFiles(newFiles);
      onFilesChange?.(newFiles.map(f => f.file));
    }
  };

  const removeFile = (fileId: string) => {
    const newFiles = files.filter(f => f.id !== fileId);
    setFiles(newFiles);
    onFilesChange?.(newFiles.map(f => f.file));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  return (
    <div className='space-y-4'>
      {/* Área de Upload */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}
          cursor-pointer
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept={extensoes.join(',')}
          onChange={e => handleFileSelect(e.target.files)}
          className='hidden'
        />

        <Upload className='h-8 w-8 mx-auto mb-2 text-gray-400' />
        <p className='text-sm text-gray-600 mb-1'>
          Arraste arquivos aqui ou clique para selecionar
        </p>
        <p className='text-xs text-gray-500'>
          {rotuloExtensoes(extensoes)} (máx. {maxSizePerFile}MB cada)
        </p>
        <p className='text-xs text-gray-400 mt-1'>
          {files.length}/{maxFiles} arquivos selecionados
        </p>
      </div>

      {/* Lista de Arquivos */}
      {files.length > 0 && (
        <div className='space-y-2'>
          <h4 className='text-sm font-medium text-gray-700'>Arquivos Selecionados</h4>
          <div className='space-y-2'>
            {files.map(file => (
              <div
                key={file.id}
                className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'
              >
                <div className='flex items-center space-x-3'>
                  <File className='h-5 w-5 text-gray-400' />
                  <div>
                    <p className='text-sm font-medium text-gray-900'>{file.name}</p>
                    <p className='text-xs text-gray-500'>
                      {formatFileSize(file.size)} • {file.type}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => removeFile(file.id)}
                  className='p-1 text-gray-400 hover:text-danger-600 transition-colors'
                  title='Remover arquivo'
                >
                  <Trash2 className='h-4 w-4' />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TempFileUpload;
